import { useId } from 'react';

/**
 * The three preview thumbnails for the theme picker (VALE-612).
 *
 * **These colours are hardcoded on purpose, and must stay that way.** Every
 * other surface in the app paints from the semantic tokens so it follows the
 * theme — this one cannot. A tile's whole job is to show what a mode looks
 * like, so the Light tile has to render light *while the app is in dark mode*,
 * and the Dark tile has to render dark while the app is in light mode. Token
 * classes would make all three tiles identical to whatever the app is currently
 * wearing, which is exactly the information the picker exists to convey.
 *
 * The lint rules that would normally object are not in play: `no-raw-palette-
 * colour` matches class-shaped strings, and `no-raw-colour-value` keys on
 * object properties and `setProperty` calls — neither reaches a JSX `fill`
 * attribute. That is a genuine gap in the rules rather than a blessing, hence
 * this note: do not use it as licence to paint anything else this way.
 *
 * Inline SVG rather than files under `public/`, because the popover must be
 * fully drawn the instant it opens — three network fetches would flash empty
 * tiles on first open — and because inline markup renders in jest and Storybook
 * with no asset pipeline.
 *
 * Artwork is transcribed verbatim from design's export (88×61, 4px radius).
 * The `system` tile is its own composition rather than the other two clipped
 * together: its dark half sits on a lighter base and swaps the sidebar tab for
 * three window dots. That asymmetry is deliberate in the source — do not
 * "correct" it into a mirror of the standalone tiles.
 */

/** Fill sets, so the two single-mode tiles share one geometry. */
const LIGHT_FILLS = {
  base: '#E5E5E5',
  surface: '#F5F5F5',
  panel: '#FFFFFF'
} as const;

const DARK_FILLS = {
  base: '#141414',
  surface: '#1F1F1F',
  panel: '#333333'
} as const;

interface TileFills {
  /** The desktop behind the window. */
  base: string;
  /** The app window itself. */
  surface: string;
  /** Content blocks inside the window — sidebar, panel, toolbar, tab. */
  panel: string;
}

/** The window chrome both single-mode tiles draw, differing only in fill. */
function WindowChrome({ base, surface, panel }: TileFills) {
  return (
    <>
      <rect y="0.5" width="88" height="60" rx="4" fill={base} />
      <path
        d="M6 10.5C6 8.29086 7.79086 6.5 10 6.5H88V60.5H6V10.5Z"
        fill={surface}
      />
      <rect x="46" y="18.5" width="40" height="40" rx="2" fill={panel} />
      <rect x="26" y="18.5" width="18" height="40" rx="2" fill={panel} />
      <rect x="26" y="1.5" width="18" height="4" rx="2" fill={panel} />
      <rect x="8" y="10.5" width="78" height="6" rx="2" fill={panel} />
    </>
  );
}

const SVG_PROPS = {
  width: 88,
  height: 61,
  viewBox: '0 0 88 61',
  fill: 'none',
  xmlns: 'http://www.w3.org/2000/svg',
  // The visible label beneath each tile is the accessible name; the artwork
  // itself is decoration and must not be announced twice.
  'aria-hidden': true,
  focusable: false
} as const;

export function ThemeTileLight({ className }: { className?: string }) {
  // `useId` because a tile can be on the page more than once (the picker plus a
  // Storybook grid), and duplicate clip-path ids silently cross-wire the clips.
  const clipId = useId();

  return (
    <svg {...SVG_PROPS} className={className}>
      <g clipPath={`url(#${clipId})`}>
        <WindowChrome {...LIGHT_FILLS} />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect y="0.5" width="88" height="60" rx="4" fill="#FFFFFF" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function ThemeTileDark({ className }: { className?: string }) {
  const clipId = useId();

  return (
    <svg {...SVG_PROPS} className={className}>
      <g clipPath={`url(#${clipId})`}>
        <WindowChrome {...DARK_FILLS} />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect y="0.5" width="88" height="60" rx="4" fill="#FFFFFF" />
        </clipPath>
      </defs>
    </svg>
  );
}

export function ThemeTileSystem({ className }: { className?: string }) {
  const id = useId();
  const roundId = `${id}-round`;
  const leftId = `${id}-left`;
  const rightId = `${id}-right`;

  return (
    <svg {...SVG_PROPS} className={className}>
      {/* Left half — light. */}
      <g clipPath={`url(#${leftId})`}>
        <g clipPath={`url(#${roundId})`}>
          <rect y="0.5" width="88" height="60" rx="4" fill="#E5E5E5" />
          <path
            d="M6 10.5C6 8.29086 7.79086 6.5 10 6.5H88V60.5H6V10.5Z"
            fill="#F5F5F5"
          />
          <rect x="26" y="18.5" width="18" height="40" rx="2" fill="#FFFFFF" />
          <rect x="26" y="1.5" width="18" height="4" rx="2" fill="#FFFFFF" />
          <rect x="8" y="10.5" width="78" height="6" rx="2" fill="#FFFFFF" />
        </g>
      </g>
      {/* Right half — dark, on its own lighter base with window dots. */}
      <g clipPath={`url(#${rightId})`}>
        <g clipPath={`url(#${roundId})`}>
          <rect y="0.5" width="88" height="60" rx="4" fill="#2E2E2E" />
          <path
            d="M6 10.5C6 8.29086 7.79086 6.5 10 6.5H88V60.5H6V10.5Z"
            fill="#141414"
          />
          <rect x="46" y="18.5" width="40" height="40" rx="2" fill="#1F1F1F" />
          <rect x="70" y="1.5" width="4" height="4" rx="2" fill="#1F1F1F" />
          <rect x="76" y="1.5" width="4" height="4" rx="2" fill="#1F1F1F" />
          <rect x="82" y="1.5" width="4" height="4" rx="2" fill="#1F1F1F" />
          <rect x="8" y="10.5" width="78" height="6" rx="2" fill="#1F1F1F" />
        </g>
      </g>
      <defs>
        <clipPath id={roundId}>
          <rect y="0.5" width="88" height="60" rx="4" fill="#FFFFFF" />
        </clipPath>
        <clipPath id={leftId}>
          <rect
            width="44"
            height="60"
            fill="#FFFFFF"
            transform="translate(0 0.5)"
          />
        </clipPath>
        <clipPath id={rightId}>
          <rect
            width="44"
            height="60"
            fill="#FFFFFF"
            transform="translate(44 0.5)"
          />
        </clipPath>
      </defs>
    </svg>
  );
}
